import React from 'react';
import { motion } from 'framer-motion';
import { Play, Layers, UserCheck, Sparkles, Brain, Clock, Zap, Rocket, BarChart3 } from 'lucide-react';
export function SolutionSlide() {
  const differentiators = [{
    icon: Clock,
    text: 'Designed for real clinical attention spans'
  }, {
    icon: Zap,
    text: 'Active engagement, not passive content'
  }, {
    icon: Rocket,
    text: 'Faster to deploy, easier to scale'
  }, {
    icon: BarChart3,
    text: 'Insight emerges from meaningful interaction'
  }];
  return <div className="h-full flex flex-col" data-id="element-441">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 h-full items-center" data-id="element-442">
        {/* Left: Value Proposition */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="col-span-1 space-y-5" data-id="element-443">
          {/* Core Insight */}
          <div data-id="element-444">
            <p className="text-sm font-medium text-purple-600 uppercase tracking-wider mb-2" data-id="element-445">
              The insight
            </p>
            <h3 className="text-xl font-bold text-gray-900 leading-snug" data-id="element-446">
              HCPs don't lack information — they lack{' '}
              <span className="text-purple-600" data-id="element-447">time</span>.
            </h3>
          </div>

          <p className="text-sm text-gray-500 leading-relaxed" data-id="element-448">
            Snakker turns complex science into short, visual, snackable
            engagement — engineered for how humans actually consume information
            — without sacrificing compliance or rigor.
          </p>

          {/* Differentiators */}
          <div className="space-y-2.5" data-id="element-449">
            {differentiators.map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            x: -10
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.3 + i * 0.1
          }} className="flex items-start gap-2.5" data-id="element-450">
                <div className="w-6 h-6 rounded-md bg-purple-100 flex items-center justify-center shrink-0 mt-0.5" data-id="element-451">
                  <item.icon className="w-3.5 h-3.5 text-purple-600" data-id="element-452" />
                </div>
                <span className="text-sm text-gray-700 leading-snug" data-id="element-453">
                  {item.text}
                </span>
              </motion.div>)}
          </div>

          {/* Snaks Format — compact */}
          <div className="p-4 rounded-xl bg-purple-50 border border-purple-200" data-id="element-454">
            <div className="flex items-center gap-2 mb-2" data-id="element-455">
              <Play className="w-4 h-4 text-purple-600 fill-purple-600" data-id="element-456" />
              <h4 className="text-sm font-bold text-gray-900" data-id="element-457">
                Snaks™ Format
              </h4>
              <span className="text-xs text-gray-400 ml-auto" data-id="element-458">
                20–40 sec episodes
              </span>
            </div>
            <div className="flex items-center justify-between text-xs font-mono text-purple-600 bg-white p-2 rounded-md border border-purple-100" data-id="element-459">
              <span data-id="element-460">Promise</span>
              <span className="text-gray-300" data-id="element-461">→</span>
              <span data-id="element-462">Action</span>
              <span className="text-gray-300" data-id="element-463">→</span>
              <span data-id="element-464">Payoff</span>
            </div>
          </div>

          {/* IQVIA badge */}
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 border border-gray-200" data-id="element-465">
            <UserCheck className="w-4 h-4 text-green-600" data-id="element-466" />
            <span className="text-xs font-medium text-gray-600" data-id="element-467">
              IQVIA Snak-Key — compliant HCP gating built-in
            </span>
          </div>
        </motion.div>

        {/* Right: Campaign Flow & Supporting Images */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.2
      }} className="col-span-2 relative flex flex-col gap-4" data-id="element-468">
          {/* Campaign flow — most prominent */}
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.3
        }} className="rounded-2xl border border-gray-200 overflow-hidden bg-white shadow-sm" data-id="element-469">
            <img src="https://cdn.magicpatterns.com/uploads/k4QN7v2sE4bx3BY29Ei3EG/image.png" alt="Turn-key Columvi Campaign: Hook → Snak → CTA → Insights iteration flow" className="w-full h-auto object-contain" data-id="element-470" />
          </motion.div>

          {/* Two supporting images side by side */}
          <div className="grid grid-cols-2 gap-3" data-id="element-471">
            <motion.div initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.45
          }} className="rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm" data-id="element-472">
              <img src="https://cdn.magicpatterns.com/uploads/6tU5XtoLPH3V2dJtHuxqt8/image.png" alt="Snak flow: Promise, Reward, Animation, Deeper Insights and Website" className="w-full h-auto object-contain" data-id="element-473" />
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.55
          }} className="rounded-xl border border-gray-200 overflow-hidden bg-white shadow-sm" data-id="element-474">
              <img src="https://cdn.magicpatterns.com/uploads/9M51vxN6xU8cSbo9XDWBXu/image.png" alt="WhatsApp distribution and multi-channel sharing options" className="w-full h-auto object-contain" data-id="element-475" />
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>;
}